import { Trans } from "@lingui/react/macro";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";

import { DataAdd } from "../../../models/data-add";
import { navigate } from "../../../utils/navigate";
import { Button } from "../button";
import { DateInput } from "../fields/date-input";
import { Form } from "../form/form";
import { RequiredAsterisk } from "../required-asterisk";
import styles from "./add-table.module.css";
import { Column as SimpleColumn, SimpleTable } from "./simple-table";

export interface Column<T> {
  header: React.ReactNode;
  cell: (item: T, index: number) => React.ReactNode;
  mandatory?: boolean;
}

export const AddTable = observer(function AddTable({
  model,
  columns,
  setAllDates,
  canAddRows,
  infoExtra,
  footer,
  onSaved,
}: {
  model: DataAdd;
  columns: Column<Record<string, any>>[];
  setAllDates?: "date" | "datetime";
  canAddRows?: boolean;
  infoExtra?: React.ReactNode;
  footer?: React.ReactNode;
  onSaved?: () => void;
}) {
  const hasMandatory = useMemo(
    () => columns.some((c) => c.mandatory),
    [columns],
  );

  return (
    <div>
      <br className="h-5" />

      {hasMandatory && (
        <>
          (<RequiredAsterisk /> <Trans>= mandatory</Trans>)
        </>
      )}
      <Form
        onSubmit={async () => {
          const ok = await model.postBulk();
          if (!ok) return;
          if (onSaved) {
            onSaved();
          } else {
            navigate(
              `/patient/${model.patient.patientId}/${model.reloadToTab}`,
            );
          }
        }}
      >
        {infoExtra}

        {setAllDates && (
          <div className={styles.datetimeSetter}>
            <p className={clsx(styles.title, "mb-4 mt-4")}>
              <strong>
                {setAllDates === "datetime" ? (
                  <Trans>Set all datetimes:</Trans>
                ) : (
                  <Trans>Set all dates:</Trans>
                )}
              </strong>
            </p>
            <p className="mb-4 mt-4">
              <Trans>(Default)</Trans> <span>{model.defaultDateTimeName}</span>
              <Button
                onClick={() => {
                  model.setAllDatetimes(model.defaultDateTime ?? "");
                }}
              >
                <Trans>Set</Trans>
              </Button>
            </p>
            <p className="mb-4 mt-4">
              <DateInput
                type={setAllDates === "date" ? "date" : "datetime-local"}
                value={model.customDateTime}
                onChange={(newValue) => {
                  model.customDateTime = newValue ?? "";
                }}
              />
              <Button
                disabled={!model.customDateTime}
                onClick={() => {
                  model.setAllDatetimes(model.customDateTime);
                }}
              >
                <Trans>Set</Trans>
              </Button>
            </p>
          </div>
        )}

        <SimpleTable
          columns={columns.map(
            (col): SimpleColumn<any> => ({
              header1: (
                <>
                  {col.header}

                  {col.mandatory && (
                    <>
                      {" "}
                      <RequiredAsterisk />
                    </>
                  )}
                </>
              ),
              cell: (item, index) => col.cell(item, index),
            }),
          )}
          rows={model.addingList.filter(Boolean)}
        />

        {canAddRows !== false && (
          <Button
            onClick={() => {
              model.addItem();
            }}
          >
            <Trans>+ Add Row</Trans>
          </Button>
        )}

        {footer && (
          <>
            <br className="h-5" />
            {footer}
          </>
        )}

        <br className="h-10" />

        <Button type="submit">
          <Trans>Post</Trans>
        </Button>
      </Form>
    </div>
  );
});
